import classnames from 'classnames';
import { IrreversibleConfirmModal } from 'components/shared/Modals/IrreversibleConfirmModal';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { addonManagementFormUncheckAllCheckbox } from 'state/addonManagementForm/addonManagementForm.action';
import {
  addonManagementFormOpenPostTradeTabsThunk,
  selectedAddonManagementFormToQuoteThunk,
  selectedAddonManagementFormToTradeThunk,
} from 'state/addonManagementForm/addonManagementForm.thunks';
import {
  canAddonManagementSelectedFormsBeExecuted,
  canAddonManagementSelectedFormsBePriced,
  getAddonManagementSelectedFormsLength,
  isSelectedFormsQuoted,
} from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import styled from 'styled-components';

export interface AddonManagementFormInteractionToolbarProps {
  classNames?: string;
}

const StyledDiv = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
`;

export function AddonManagementFormInteractionToolbar({ classNames }: AddonManagementFormInteractionToolbarProps) {
  const dispatch = useDispatch<ThunkDispatch>();
  const selectedFormsLength = useSelector(getAddonManagementSelectedFormsLength);

  const [isQuotedModalOpened, setIsQuotedModalOpened] = useState(false);
  const openIsQuotedModal = useCallback(() => setIsQuotedModalOpened(true), []);
  const closeIsQuotedModal = useCallback(() => setIsQuotedModalOpened(false), []);
  const onPriceFormsConfirmed = useCallback(() => {
    logAnalytics.trackEvent('Addon Management Workspace', 'Request for tradable quote');
    dispatch(selectedAddonManagementFormToQuoteThunk());
    closeIsQuotedModal();
  }, [closeIsQuotedModal, dispatch]);

  const isQuoted = useSelector(isSelectedFormsQuoted);
  const canPriceSelectedItems = useSelector(canAddonManagementSelectedFormsBePriced);
  const onPrice = useCallback(() => {
    if (isQuoted) {
      openIsQuotedModal();
    } else {
      onPriceFormsConfirmed();
    }
  }, [isQuoted, onPriceFormsConfirmed, openIsQuotedModal]);

  const canExecuteSelectedItems = useSelector(canAddonManagementSelectedFormsBeExecuted);
  const onExecute = useCallback(() => {
    logAnalytics.trackEvent('Addon Management Workspace', 'Request for trade');
    dispatch(selectedAddonManagementFormToTradeThunk());
  }, [dispatch]);

  return (
    <StyledDiv
      className={classnames(classNames, 'd-flex align-items-center justify-content-between p-2')}
      data-e2e="addon-management-form-interaction-toolbar-div"
    >
      <div>
        <span className="badge badge-lg rounded-pill badge-primary-alt mx-2 px-2" data-e2e="selected-form-number-label">
          {selectedFormsLength}
        </span>
        <span>selected</span>
      </div>
      <div>
        <button
          className="btn btn-lg btn-flat-primary-alt"
          onClick={() => dispatch(addonManagementFormUncheckAllCheckbox())}
          type="button"
          data-e2e="addon-management-clear-form-button"
        >
          <FormattedMessage id="addonManagement.button.clear" />
        </button>

        <button
          className="btn btn-lg btn-flat-primary-alt"
          onClick={() => dispatch(addonManagementFormOpenPostTradeTabsThunk())}
          type="button"
          data-e2e="addon-management-open-form-button"
        >
          <FormattedMessage id="addonManagement.button.openInPostTradeWorkspace" />
          <i className="icon icon-sm ms-2">launch</i>
        </button>

        <button
          className="btn btn-lg btn-outline-primary-alt ms-2"
          disabled={!canPriceSelectedItems}
          type="button"
          onClick={onPrice}
          data-e2e="addon-management-price-form-button"
        >
          <FormattedMessage id="addonManagement.button.price" />
        </button>

        <button
          className="btn btn-lg btn-outline-primary-alt ms-1"
          disabled={!canExecuteSelectedItems}
          onClick={onExecute}
          type="button"
          data-e2e="addon-management-execute-form-button"
        >
          <FormattedMessage id="addonManagement.button.execute" />
        </button>

        <IrreversibleConfirmModal
          title={<FormattedMessage id="addonManagement.repriceAddOn.title" />}
          onConfirm={onPriceFormsConfirmed}
          onClosed={closeIsQuotedModal}
          isOpen={isQuotedModalOpened}
          description={<FormattedMessage id="addonManagement.repriceAddOn.description" />}
        />
      </div>
    </StyledDiv>
  );
}
